<template>
  <!-- Só pra quem tem acesso ao Agendor (ligado de modo geral + agente atribuído em Admin › Agendor). -->
  <span v-if="visible" class="ag" :class="[`ag--${variant}`, { 'ag--on': !!link }]">
    <button
      ref="btn"
      type="button"
      class="ag-btn"
      :title="title"
      :aria-label="title"
      :aria-expanded="open"
      @click.stop="toggle"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 13h18" />
      </svg>
      <span v-if="label && link" class="ag-label">Agendor</span>
    </button>

    <Teleport to="body">
      <div v-if="open" ref="pop" class="ag-pop" :style="popStyle" role="dialog" aria-label="Agendor" @click.stop>
        <div class="ag-pop-head">
          <span class="ag-pop-brand">Agendor</span>
          <button type="button" class="ag-x" aria-label="Fechar" @click="open = false">×</button>
        </div>

        <template v-if="link">
          <div class="ag-name">{{ link.personName }}</div>
          <dl class="ag-facts">
            <template v-if="link.organizationName"><dt>Empresa</dt><dd>{{ link.organizationName }}</dd></template>
            <dt>Responsável</dt><dd>{{ link.ownerName || '—' }}</dd>
            <dt>Origem</dt><dd>{{ ORIGINS[link.origin] }}</dd>
            <dt>Atualizado</dt><dd>{{ ago(link.syncedAt) }}</dd>
          </dl>
          <div class="ag-actions">
            <a v-if="link.webUrl" class="ag-act ag-act--main" :href="link.webUrl" target="_blank" rel="noopener">Ver no Agendor ↗</a>
            <button type="button" class="ag-act" :disabled="busy" @click="find">{{ busy ? 'Atualizando…' : 'Atualizar' }}</button>
            <button type="button" class="ag-act ag-act--danger" :disabled="busy" @click="unlink">Desvincular</button>
          </div>
        </template>

        <template v-else-if="candidates.length">
          <p class="ag-note">Mais de uma pessoa no Agendor com este telefone. Qual é este contato?</p>
          <ul class="ag-cands">
            <li v-for="c in candidates" :key="c.id">
              <button type="button" class="ag-cand" :disabled="busy" @click="choose(c.id)">
                <b>{{ c.name }}</b>
                <small>{{ [c.organizationName, c.ownerName ? `resp. ${c.ownerName}` : null].filter(Boolean).join(' · ') || `id ${c.id}` }}</small>
              </button>
            </li>
          </ul>
        </template>

        <template v-else>
          <p class="ag-note">{{ notFound ? 'Nenhum cadastro no Agendor com o telefone deste contato.' : 'Contato ainda não vinculado ao Agendor.' }}</p>
          <div class="ag-actions">
            <button type="button" class="ag-act ag-act--main" :disabled="busy" @click="find">{{ busy ? 'Procurando…' : 'Procurar pelo telefone' }}</button>
          </div>
        </template>

        <p v-if="problem" class="ag-problem">{{ problem }}</p>
      </div>
    </Teleport>
  </span>
</template>

<script setup lang="ts">
// Ícone do Agendor (plano-agendor.md, 3.1): preenchido = contato vinculado (tooltip com pessoa, empresa e
// responsável); vazado = não vinculado (só onde `showUnlinked`, ex: cabeçalho do chat). Clique abre o painel com
// Ver no Agendor, Atualizar, Desvincular ou Procurar pelo telefone.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { loadAgendor, requestAgendorLink, setAgendorLink, useAgendor } from '@/composables/useAgendor'
import { findAgendorContact, linkAgendorPerson, unlinkAgendorContact, type AgendorActionResult, type AgendorPerson } from '@/services/wootdashAgendor'

const props = withDefaults(defineProps<{
  contactId: string | null | undefined
  conversationId?: string | null
  variant?: 'row' | 'dark' | 'light'
  /** Mostra o ícone vazado quando não há vínculo (pra poder procurar). */
  showUnlinked?: boolean
  /** Texto "Agendor" ao lado do ícone. */
  label?: boolean
}>(), { conversationId: null, variant: 'row', showUnlinked: false, label: false })

const ORIGINS: Record<string, string> = { Bot: 'Cadastrado pelo bot', Agent: 'Vinculado por um agente', PhoneMatch: 'Achado pelo telefone' }

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const { access, links } = useAgendor()

const link = computed(() => (props.contactId ? links.get(props.contactId) ?? null : null))
const visible = computed(() => !!access.value?.allowed && !!props.contactId && (!!link.value || props.showUnlinked))
const title = computed(() => {
  const l = link.value
  if (!l) return 'Agendor: não vinculado — clique pra procurar'
  return ['Agendor', l.personName, l.organizationName, l.ownerName ? `responsável ${l.ownerName}` : null].filter(Boolean).join(' · ')
})

const open = ref(false)
const busy = ref(false)
const problem = ref<string | null>(null)
const notFound = ref(false)
const candidates = ref<AgendorPerson[]>([])
const btn = ref<HTMLElement | null>(null)
const pop = ref<HTMLElement | null>(null)
const popStyle = ref<Record<string, string>>({})

function ensure() {
  void loadAgendor(token.value).then(() => requestAgendorLink(props.contactId))
}
onMounted(ensure)
watch(() => props.contactId, () => {
  ensure()
  open.value = false
})
watch(() => access.value?.allowed, allowed => { if (allowed) requestAgendorLink(props.contactId) })

function place() {
  const r = btn.value?.getBoundingClientRect()
  if (!r) return
  const width = 280
  const left = Math.max(8, Math.min(window.innerWidth - width - 8, r.left))
  popStyle.value = { top: `${r.bottom + 6}px`, left: `${left}px`, width: `${width}px` }
}

function toggle() {
  open.value = !open.value
  problem.value = null
  candidates.value = []
  notFound.value = false
  if (open.value) {
    place()
    void nextTick(() => pop.value?.querySelector<HTMLElement>('button, a')?.focus())
  }
}

function onOutside(e: MouseEvent) {
  if (open.value && !pop.value?.contains(e.target as Node) && !btn.value?.contains(e.target as Node)) open.value = false
}
function onKey(e: KeyboardEvent) { if (e.key === 'Escape') open.value = false }
onMounted(() => { document.addEventListener('click', onOutside); document.addEventListener('keydown', onKey); window.addEventListener('scroll', place, true) })
onBeforeUnmount(() => { document.removeEventListener('click', onOutside); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', place, true) })

function apply(result: AgendorActionResult) {
  problem.value = result.problem ?? result.match?.problem ?? null
  const match = result.match
  if (!result.allowed || !props.contactId || !match) return
  if (match.outcome === 'Linked' || match.outcome === 'AlreadyLinked') setAgendorLink(props.contactId, match.link)
  candidates.value = match.outcome === 'Ambiguous' ? match.candidates ?? [] : []
  notFound.value = match.outcome === 'NotFound'
  if (match.outcome === 'NoPhone') problem.value = 'O contato não tem telefone.'
}

async function find() {
  if (!props.contactId) return
  busy.value = true
  try { apply(await findAgendorContact(token.value, props.contactId, props.conversationId)) } finally { busy.value = false }
}

async function choose(personId: string) {
  if (!props.contactId) return
  busy.value = true
  try { apply(await linkAgendorPerson(token.value, props.contactId, personId, props.conversationId)) } finally { busy.value = false }
}

async function unlink() {
  if (!props.contactId || !link.value) return
  const ok = await Swal.fire({
    title: 'Desvincular do Agendor?',
    text: `O contato deixa de estar ligado a ${link.value.personName}. Nada é apagado no Agendor.`,
    icon: 'warning', showCancelButton: true, confirmButtonText: 'Desvincular', cancelButtonText: 'Cancelar',
  })
  if (!ok.isConfirmed) return
  busy.value = true
  try {
    const result = await unlinkAgendorContact(token.value, props.contactId, props.conversationId)
    problem.value = result.problem
    if (result.allowed && !result.problem) setAgendorLink(props.contactId, null)
  } finally { busy.value = false }
}

function ago(iso: string) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (minutes < 1) return 'agora'
  if (minutes < 60) return `há ${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `há ${hours} h`
  return `há ${Math.round(hours / 24)} dia(s)`
}
</script>

<style scoped>
.ag { display: inline-flex; align-items: center; vertical-align: middle; }
.ag-btn {
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 5px; border-radius: 6px; cursor: pointer;
  border: 1px dashed currentColor; background: transparent; color: #94a3b8; font: inherit; font-size: 11px; font-weight: 600; line-height: 1;
}
.ag--on .ag-btn { border-style: solid; border-color: transparent; background: #e0f0ed; color: #0f6f66; }
.ag--dark .ag-btn { color: rgba(255, 255, 255, .7); }
.ag--dark.ag--on .ag-btn { background: rgba(79, 193, 179, .2); color: #8be3d6; }
.ag-btn:hover { filter: brightness(.95); }
.ag-btn:focus-visible { outline: 2px solid #0f6f66; outline-offset: 1px; }
.ag-label { letter-spacing: .02em; }

.ag-pop {
  position: fixed; z-index: 4000; background: var(--white, #fff); color: var(--ink-80, #1f2937); border: 1px solid var(--ink-10, #e5e7eb);
  border-radius: 10px; box-shadow: 0 10px 30px rgba(15, 23, 42, .18); padding: 12px 14px; display: flex; flex-direction: column; gap: 8px;
  font-size: 13px; text-align: left;
}
.ag-pop-head { display: flex; align-items: center; justify-content: space-between; }
.ag-pop-brand { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #0f6f66; }
.ag-x { border: none; background: none; font-size: 18px; line-height: 1; color: var(--ink-40, #9ca3af); cursor: pointer; padding: 0 2px; }
.ag-name { font-size: 15px; font-weight: 700; }
.ag-facts { display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; margin: 0; }
.ag-facts dt { color: var(--ink-40, #6b7280); font-size: 12px; }
.ag-facts dd { margin: 0; font-size: 12.5px; min-width: 0; overflow-wrap: anywhere; }
.ag-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 2px; }
.ag-act {
  border: 1px solid var(--ink-10, #e5e7eb); background: transparent; color: var(--ink-70, #374151); border-radius: 6px; padding: 5px 10px;
  font: inherit; font-size: 12px; font-weight: 600; cursor: pointer; text-decoration: none;
}
.ag-act:hover:not(:disabled) { background: var(--ink-05, #f3f4f6); }
.ag-act:disabled { opacity: .5; cursor: not-allowed; }
.ag-act--main { background: #0f6f66; border-color: #0f6f66; color: #fff; }
.ag-act--main:hover:not(:disabled) { background: #0b5a53; }
.ag-act--danger { color: #b91c1c; }
.ag-note { margin: 0; color: var(--ink-60, #4b5563); font-size: 12.5px; line-height: 1.45; }
.ag-problem { margin: 0; color: #b45309; font-size: 12px; }
.ag-cands { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; max-height: 220px; overflow: auto; }
.ag-cand {
  width: 100%; text-align: left; border: 1px solid var(--ink-10, #e5e7eb); background: transparent; border-radius: 6px; padding: 6px 8px;
  display: flex; flex-direction: column; gap: 1px; cursor: pointer; font: inherit; color: inherit;
}
.ag-cand:hover:not(:disabled) { border-color: #0f6f66; }
.ag-cand small { color: var(--ink-40, #6b7280); font-size: 11.5px; }
</style>
